import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { formValueSelector } from 'redux-form';
import { Row, Col, Card, CardBody, CardTitle, CardText } from 'reactstrap';
import TickettypeFieldForm from '../TickettypeFieldForm/TickettypeFieldForm';
import CardIconAction from '../CardIconAction/CardIconAction';
import RedundantOverridesAlert from '../RedundantOverridesAlert';
import { find_redundant_fields_overrides } from '../../services/redundantOverrides';
import { EMPTY_OBJECT, Utils } from '../../services';

import '../CardIconAction/CardIconAction.scss';

const formSelector = formValueSelector('tickettypeForm');

const FieldsForm = ({ salepoints, remove, addControl }) => {
    const { t, i18n } = useTranslation();
    const { tickettype }  = useSelector(state => state.tickettypes);
    const fieldsFormValues = useSelector(state => formSelector(state, 'opaque.fields') || EMPTY_OBJECT);
    const [persistedOverrideIds, setPersistedOverrideIds] = useState([]);

    // Only IDs present when the tickettype was loaded/switched are "persisted".
    useEffect(() => {
        const loaded = Object.keys(tickettype?.opaque?.fields || {}).filter(id => id !== 'default');
        setPersistedOverrideIds(loaded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickettype?._id]);

    const salepointName = (id) => {
        if (id === "default")
            return t('salepoints.default');
        const salepoint = salepoints.find((item) => String(item._id) === String(id));
        return Utils.localized_or_fallback(salepoint?.name, i18n.language, id);
    }

    const fieldIds = Object.keys(fieldsFormValues).length
        ? Object.keys(fieldsFormValues)
        : Object.keys(tickettype.opaque?.fields || {});
    const defaultId = fieldIds.includes('default') ? 'default' : null;
    const overrideIds = fieldIds.filter(id => id !== 'default');

    const redundantItems = useMemo(() => (
        find_redundant_fields_overrides(fieldsFormValues, persistedOverrideIds)
            .map(id => ({ id, label: salepointName(id), icon: 'fa-map-marker' }))
    // salepointName depends on salepoints/t which are stable enough for this list
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [fieldsFormValues, persistedOverrideIds, salepoints, t, i18n.language]);

    const cardTitle = (salepoint) => (
        salepoint === 'default'
            ? salepointName(salepoint)
            : t('tickettypes.field_overrides.on_salepoint', { salepoint: salepointName(salepoint) })
    );

    const renderCard = (salepoint) => (
        <Col xs="6" key={salepoint} className="mb-3">
            <Card className="CardShell">
                <CardBody>
                    <CardTitle className="d-flex align-items-center justify-content-start w-100 text-left">
                        {salepoint !== "default" && (
                            <span className="CardIconActions CardIconActions--inline CardIconActions--inline-start mb-0">
                                <CardIconAction
                                    id={`delete-field-${salepoint}`}
                                    icon="fa-trash"
                                    danger
                                    title={t('common.card_action_delete_field_override')}
                                    onClick={() => remove(salepoint)}
                                />
                            </span>
                        )}
                        <span>
                            {salepoint === 'default' && <><i className="fa fa-map-marker"></i>{' '}</>}
                            {cardTitle(salepoint)}
                        </span>
                    </CardTitle>
                    <CardText tag="div">
                        <TickettypeFieldForm salepointId={salepoint} />
                    </CardText>
                </CardBody>
            </Card>
        </Col>
    );

    return (
        <>
            <div className="FieldsForm-addOverride mb-3">
                <RedundantOverridesAlert
                    items={redundantItems}
                    onRemove={remove}
                />
            </div>
            {defaultId && (
                <Row>
                    {renderCard(defaultId)}
                </Row>
            )}
            {addControl}
            {overrideIds.length > 0 && (
                <Row>
                    {overrideIds.map(renderCard)}
                </Row>
            )}
        </>
    );
}

export default FieldsForm;
